/* global Sys, Platform, Game, Utils, Services */
Sys.ns("Sys");

/**
 * The static Environment class is responsible for keeping track of
 * all environmental properties and changes (like orientation and viewport size).
 *
 * Instantiates itself.
 *
 * @class Sys.Environment
 * @extends Sys.Observable
 * @static
 */
Sys.Environment = {

    constructor: function(config) {
        Sys.Environment.superclass.constructor.apply(this, arguments);

        this.defaultResolutions = {
            mobile: {
                "default": {
                    width: 960,
                    height: 540,
                    pixelFactor: 1,
                    portraitTopOffset: 0.3
                }
            },
            tablet: {
                "default": {
                    width: 960,
                    height: 540,
                    pixelFactor: 1
                }
            },
            desktop: {
                "default": {
                    width: 1280,
                    height: 720,
                    pixelFactor: 1.33
                }
            }
        };

        this.defaultVirtualResolutions = {
            "default": {
                "default": {
                    width: 1280,
                    height: 720
                }
            }
        };

        this.onResized();
        this.setupEvents();
    },

    /**
     * @listens notify:platform.resized
     * @returns {void}
     */
    setupEvents: function() {
        this.on({
            "notify:platform.resized": this.onResized
        });
    },

    /**
     * Is called when an resize change is in effect, prompted either by the device or the system.
     *
     * @private
     * @returns {void}
     */
    onResized: function() {
        document.documentElement.style.fontSize = parseInt(this.determineResolution().resolution.pixelFactor * 100, 10) + "px";
    },

    /**
     * Get the platform specific screen resolution used.
     *
     * @deprecated 8.0.0. Use Environment.determineResolution().resolution instead.
     * @returns {{width: number, height: number}} The resolution.
     */
    getResolution: function() {
        return this.determineResolution().resolution;
    },

    /**
     * Get the virtual stage resolution.
     *
     * @deprecated 8.0.0. Use Environment.determineResolution().virtualResolution instead.
     * @returns {{width: number, height: number}} The dimensions of the stage.
     */
    getStageResolution: function() {
        return this.determineResolution().virtualResolution;
    },

    /**
     * Getter for checking if custom canvas size is allowed.
     *
     * @returns {boolean} Returns true if custom canvas size is allowed.
     */
    allowsCustomCanvasSize: function() {
        return Boolean(window.RESOLUTIONS_CONFIG && window.VIRTUAL_RESOLUTIONS_CONFIG);
    },

    /**
     * Get the current viewport orientation. Note: this might differ from the actual device's orientation.
     *
     * @deprecated 8.0.0. Use Utils.Platform.getOrientation() instead.
     * @param {boolean} [useScreenOrientation] Whether or not to use the screen orientation instead of the viewport orientation.
     * @returns {string} Returns PORTRAIT or LANDSCAPE.
     */
    getViewportOrientation: function(useScreenOrientation) {
        if (this.allowsCustomCanvasSize() && !useScreenOrientation) {
            return this.viewportOrientation;
        }

        return this.orientation();
    },

    /**
     * Get the current resolutions pixel factor .
     *
     * @deprecated 8.0.0. Use Environment.determineResolution().resolution.pixelFactor instead.
     * @returns {number} Returns the current pixel factor.
     */
    getCurrentResolutionPixelFactor: function() {
        return this.determineResolution().resolution.pixelFactor;
    },

    /**
     * Convert the value from virtual scale to window scale.
     *
     * @deprecated 8.0.0. Use toFixed and parseFloat.
     * @param {number} value The virtual number to scale.
     * @param {number} [dec= 0] The optional number of decimals to use (defaults to zero).
     * @returns {number} The scaled value.
     */
    scaleValue: function(value, dec) {
        var decimals = Sys.isNumber(dec) ? dec : 0;

        return parseFloat(value.toFixed(decimals));
    },

    /**
     * Calculate the window x-coordinate from the virtual coordinate.
     *
     * @deprecated 8.0.0. Use toFixed and parseFloat.
     * @param {number} x The virtual x-coordinate.
     * @param {number} [decimals= 0] The optional number of decimals to use (defaults to zero).
     * @returns {number} The scaled value.
     */
    scaleX: function(x, decimals) {
        return this.scaleValue(x, decimals);
    },

    /**
     * Calculate the window y-coordinate from the virtual coordinate.
     *
     * @deprecated 8.0.0. Use toFixed and parseFloat.
     * @param {number} y The virtual y-coordinate.
     * @param {number} [decimals= 0] The optional number of decimals to use (defaults to zero).
     * @returns {number} The scaled value.
     */
    scaleY: function(y, decimals) {
        return this.scaleValue(y, decimals);
    },

    /**
     * Convert the value from virtual scale to window scale.
     *
     * @deprecated 8.0.0. Use Environment.determineResolution().virtualToWindowScale instead.
     * @returns {number} The scaled value.
     */
    getVirtualToWindowScale: function() {
        return this.determineResolution().virtualToWindowScale;
    },

    /**
     * Convert the value from window scale to virtual scale.
     *
     * @deprecated 8.0.0. Use Environment.determineResolution().windowToVirtualScale instead
     * @param {number} [decimals= 0] The optional number of decimals to use (defaults to zero).
     * @returns {number} The scaled value.
     */
    getWindowToVirtualScale: function(decimals) {
        return parseFloat((1 / this.determineResolution().virtualToWindowScale).toFixed(decimals));
    },

    /**
     * Update the resolution properties, for example on orientation change.
     *
     * @deprecated 8.0.0. Not in use.
     * @returns {void}
     */
    updateResolutionProperties: function() {
        this.resolutionProperties = this.determineResolution();
        document.documentElement.style.fontSize = this.resolutionProperties.resolution.pixelFactor * 100 + "px";
    },

    /**
     * We save the initial screen size so we can check if the orientation has changed before all modules are loaded.
     *
     * @deprecated 8.0.0. Use Utils.Platform methods to determine size changes instead.
     * @returns {Object} The screen size when the enviroment is loaded.
     */
    getInitialScreenSize: function() {
        return this.initialScreenSize;
    },

    /**
     * @deprecated 8.0.0. Check if portrait with Utils.Platform.isPortrait() instead.
     * @returns {void}
     */
    setPortraitSupport: function() {
        this.portraitSupport = false;
    },

    /**
     * @deprecated 8.0.0. Check if portrait with Utils.Platform.isPortrait() instead.
     * @returns {boolean} True or false.
     */
    isPortraitSupported: function() {
        return this.portraitSupport;
    },

    /**
     * Identify the OS.
     *
     * @private
     * @deprecated 8.0.0. Use feature detection or OS detection in PlatformManager instead.
     * @returns {void}
     */
    identifyOS: function() {
        var userAgent = navigator.userAgent,
            os = "";

        if (userAgent.match(/Windows/i)) {
            os = "windows";
        }
        else if (userAgent.match(/Android/i)) {
            os = "android";
        }
        else if (userAgent.match(/iPad/i) || userAgent.match(/iPhone/i) || userAgent.match(/iPod/i)) {
            os = "ios";
        }

        this.os = os;
    },

    /**
     * Identify the browser.
     *
     * @private
     * @deprecated 8.0.0. Use feature detection or browser detection in PlatformManager instead.
     * @returns {void}
     */
    identifyBrowser: function() {
        var userAgent = navigator.userAgent,
            os = this.os,
            browser = "";

        if (userAgent.match(/CriOS/i) || userAgent.match(/Chrome/i)) {
            browser = "chrome";
        }
        else if (userAgent.match(/MSIE [0-9]*\.[0-9]*;/i)) {
            browser = "ie";
        }
        else if (userAgent.match(/Safari/i)) {
            if (os === "ios") {
                browser = "safari";
            }
            else if (os === "android") {
                browser = "stock";
            }
        }
        else if (userAgent.match(/Firefox/i)) {
            browser = "firefox";
        }

        this.browser = browser;
    },

    /**
     * Identify the platform.
     *
     * @private
     * @deprecated 8.0.0. Use Environment.getCurrentPlatform() instead.
     * @returns {void}
     */
    identifyPlatform: function() {
        var platform = "mobile";

        if (Platform.isTabletDevice) {
            platform = "tablet";
        }
        else if (Platform.isDesktopDevice) {
            platform = "desktop";
        }

        this.platformCSS = platform;
        if (platform !== "desktop" && Platform.isLowEndDevice) {
            platform += "Low";
        }

        this.platform = platform;
    },

    /**
     * Returns the current orientation in capital letters.
     *
     * @deprecated 8.0.0. Use Utils.Platform.getOrientation() instead.
     * @returns {string} The string "LANDSCAPE" or "PORTRAIT".
     */
    getOrientation: function() {
        // If we have a specified orientation (set by the postman)
        if (Sys.isDefined(this.deviceOrientation)) {
            return this.deviceOrientation;
        }

        if (Platform.isDesktopDevice || (!this.allowsCustomCanvasSize() && Platform.isTabletDevice)) {
            return "LANDSCAPE";
        }

        if (Platform.isAndroidStockBrowser) {
            if (Math.abs(window.orientation) === 90) {
                return "LANDSCAPE";
            }

            return "PORTRAIT";
        }

        if (window.innerWidth >= window.innerHeight) {
            return "LANDSCAPE";
        }

        return "PORTRAIT";
    },

    /**
     * Returns the actual orientation for a device, in capital letters.
     *
     * @deprecated 8.0.0. Use Utils.Platform.getOrientation() instead.
     * @returns {string} The string "LANDSCAPE" or "PORTRAIT".
     */
    getDeviceOrientation: function() {
        if (window.innerWidth >= window.innerHeight) {
            return "LANDSCAPE";
        }

        return "PORTRAIT";
    },

    /**
     * Determines the resolution.
     *
     * @private
     * @returns {{virtualResolution: {width: number, height: number, aspect: number}, resolution: *, virtualToWindowScale: number}} The resolution properties.
     */
    determineResolution: function() {
        var resolutions = {
                standard: window.RESOLUTIONS_CONFIG || this.defaultResolutions,
                virtual: window.VIRTUAL_RESOLUTIONS_CONFIG || this.defaultVirtualResolutions
            },
            virtualResolution = this.getConfigForCurrentDeviceState(resolutions.virtual, false, true),
            resolution = this.getClosestResolution(resolutions.standard, virtualResolution),
            virtualToWindowScale = resolution.height / virtualResolution.height;

        return {
            virtualResolution: virtualResolution,
            resolution: resolution,
            virtualToWindowScale: virtualToWindowScale,
            windowToVirtualScale: 1 / virtualToWindowScale,
            portraitTopOffset: resolution.portraitTopOffset || 0
        };
    },

    /**
     * Return the key for the closest number in an array.
     *
     * @deprecated 8.0.0. Not in use.
     * @param {number} number The number to match against.
     * @param {Array} array The array containing key/value pair where value is a number to compare with.
     * @returns {*} Returns the key for the matching value.
     */
    getKeyOfClosestResolution: function(number, array) {
        var current = array[0].value,
            currentKey = array[0].key,
            difference = Math.abs(number - current),
            index = array.length,
            newDifference;

        while (index--) {
            newDifference = Math.abs(number - array[index].value);

            if (newDifference < difference) {
                difference = newDifference;
                currentKey = array[index].key;
            }
        }
        return currentKey;
    },

    /**
     * Find the best matching resolution based on the aspect ratio of the resolution and the virtual resolution used for the current platform.
     *
     * @param {Object} resolutions All resolutions for the current platform.
     * @param {Object} virtualResolution A virtual resolution to compare with.
     * @returns {Object} Returns the best matching resolution.
     */
    getClosestResolution: function(resolutions, virtualResolution) {
        var platformResolutions = this.getPlatformSpecificConfig(resolutions),
            examinedResolution = {};

        if (platformResolutions === null || Object.keys(platformResolutions).length === 0) {
            console.warn("No resolutions found. Check the documentation.");
            return null;
        }

        Sys.iterate(platformResolutions, function(key, resolution) {
            examinedResolution[key] = {
                source: resolution,
                width: resolution.width,
                height: resolution.height,
                ratio: resolution.width / resolution.height
            };
        });

        return this.findClosestResolution(virtualResolution, examinedResolution);
    },

    /**
     * Find the closest matching resolution based on the viewport size.
     * We use the different in size and ratio to calculate the best match.
     * Basically we take the difference in pixels for the X and Y axis and multiply it with the
     * difference for the ratio from a base of 1.
     *
     * @param {Object} target The current virtual resolution.
     * @param {Object} sourceResolutions All resolutions to match against.
     * @returns {Object} Returns the best matching resolution.
     */
    findClosestResolution: function(target, sourceResolutions) {
        var targetResolution = {
                source: target,
                width: target.width,
                height: target.height,
                ratio: target.width / target.height
            },
            resolutions = [],
            sortByDiffAsc = function(curr, next) {
                if (Math.abs(curr.diff) < Math.abs(next.diff)) {
                    return -1;
                }
                else if (Math.abs(curr.diff) > Math.abs(next.diff)) {
                    return 1;
                }

                return 0;
            };

        // Calculate
        Sys.iterate(sourceResolutions, function(key, sourceResolution) {
            var resolution = sourceResolution,
                xDiff,
                yDiff,
                resolutionDiff,
                ratioDiff;

            xDiff = Math.abs(resolution.width - targetResolution.width);
            yDiff = Math.abs(resolution.height - targetResolution.height);
            resolutionDiff = xDiff + yDiff;
            ratioDiff = Math.abs(resolution.ratio - targetResolution.ratio);

            resolution.diff = resolutionDiff * (1 + ratioDiff);

            resolutions.push(resolution);
        });

        // Sort by the calculated diff. Smallest diff is the best match.
        resolutions.sort(sortByDiffAsc);

        return resolutions[0].source;
    },

    /**
     * Examines the resolution and and ratio so we can use it in out calculations.
     *
     * @deprecated 8.0.0. Retrieve the values you want directly instead.
     * @param {Object} resolution The resolution you want to examine.
     * @returns {Object} Returns the examined resolution.
     */
    examineResolution: function(resolution) {
        return {
            source: resolution,
            width: resolution.width,
            height: resolution.height,
            ratio: resolution.width / resolution.height
        };
    },

    /**
     * Gets the config for the specific platform. It will default to device specific config if the current doesn't exists.
     * E.g. if current platform is mobileLow and config only exists for mobile. It will default to mobile.
     * If no specific config is added at all for any platform it will use the default config for all platforms.
     *
     * @param {Object} sourceConfig The source config.
     * @param {Object} sourceConfig.default The default platform configuration.
     * @param {Object} [sourceConfig.mobile] The mobile specific platform configuration.
     * @param {Object} [sourceConfig.mobileLow] The mobile low specific platform configuration.
     * @param {Object} [sourceConfig.tablet] The tablet specific platform configuration.
     * @param {Object} [sourceConfig.desktop] The desktop specific platform configuration.
     * @param {boolean} [useUpperCase] Whether or not to use upper case keys for identifying the platform.
     * @returns {Object|null}  The platform specific config.
     */
    getPlatformSpecificConfig: function(sourceConfig, useUpperCase) {
        var platform = this.getCurrentPlatform(),
            platformSuffix = "Low",
            fallback = "default";

        if (useUpperCase) {
            platform = platform.toUpperCase();
            platformSuffix = platformSuffix.toUpperCase();
            fallback = fallback.toUpperCase();
        }

        return sourceConfig[platform] || sourceConfig[platform.replace(platformSuffix, "")] || sourceConfig[fallback] || null;
    },

    /**
     * Gets the config for the current orientation from the platform specific config.
     * If no platform specific config is provided it will use the default config for all platforms.
     *
     * @param {Object} platformConfig The source config.
     * @param {Object} platformConfig.default The default orientation configuration.
     * @param {Object} [platformConfig.landscape] The landscape specific orientation configuration.
     * @param {Object} [platformConfig.portrait] The portrait specific orientation configuration.
     * @param {boolean} [useUpperCase] Whether or not to use upper case keys for identifying the orientation.
     * @returns {Object|null} The orientation specific config.
     */
    getOrientationSpecificConfig: function(platformConfig, useUpperCase) {
        var orientation = Utils.Platform.getOrientation(),
            fallbackKey = "default",
            baseKey = "base",
            configWithBase = {};

        if (!Sys.isDefined(platformConfig) || platformConfig === null) {
            return null;
        }

        if (!useUpperCase) {
            orientation = orientation.toLowerCase();
            baseKey = baseKey.toLowerCase();
        }
        else {
            fallbackKey = fallbackKey.toUpperCase();
            baseKey = baseKey.toUpperCase();
        }

        // If a base orientation is used we apply it here.
        if (Sys.isDefined(platformConfig[baseKey])) {
            configWithBase = Sys.applyProperties(configWithBase, platformConfig[baseKey]);
            configWithBase = Sys.applyProperties(configWithBase, platformConfig[orientation] || {});
            return configWithBase;
        }
        return platformConfig[orientation] || platformConfig[fallbackKey] || null;
    },

    /**
     * Get the config for the current platform and orientation or default to the config for all platforms and orientations.
     *
     * @param {Object} sourceConfig The source config.
     * @param {Object} sourceConfig.default The default platform configuration.
     * @param {Object} sourceConfig.default.default The default orientation configuration.
     * @param {Object} [sourceConfig.mobile] The mobile specific platform configuration.
     * @param {Object} [sourceConfig.mobile.portrait] The portrait specific orientation configuration.
     * @param {boolean} [useUpperCase] Whether or not to use upper case keys for identifying the device state.
     * @returns {Object|null} The config for the current device and state.
     */
    getConfigForCurrentDeviceState: function(sourceConfig, useUpperCase) {
        var platformConfig = this.getPlatformSpecificConfig(sourceConfig, useUpperCase);

        return this.getOrientationSpecificConfig(platformConfig, useUpperCase);
    },

    /**
     * Returns the matching virtual resolution based on current platform and orientation.
     *
     * @deprecated 8.0.0. Use Environment.getConfigForCurrentDeviceState() instead.
     * @param {Object} resolutions A list of all virtual resolutions.
     * @param {Object} resolutions.default The default platform configuration.
     * @param {Object} resolutions.default.default The default orientation configuration.
     * @param {Object} [resolutions.mobile] The mobile specific platform configuration.
     * @param {Object} [resolutions.mobile.portrait] The portrait specific orientation configuration.
     * @returns {Object|null} The resolution object for the matching resolution.
     */
    getVirtualResolution: function(resolutions) {
        var virtualResolution = this.getConfigForCurrentDeviceState(resolutions, false, true);

        if (virtualResolution !== null) {
            return virtualResolution;
        }

        // Default resolution
        console.error("No default virtual resolution provided. Please check documentation.");
        return null;
    },

    /**
     * Returns the top value to set if you want your element to be placed the given percentage down within
     * the space above the game (provided that your element resides within the gameWrapper div).
     *
     * @param {number} percentage The percentage of the top area height as a decimal value.
     * @returns {number} The pixel value you should set.
     */
    getTopAboveGame: function(percentage) {
        var spaceAboveGame = this.getSpaceAboveGame();

        return Math.round(spaceAboveGame * percentage - spaceAboveGame);
    },

    /**
     * Returns the top value to set if you want your element to be placed the given percentage down within
     * the game area (provided that your element resides within the gameWrapper div).
     *
     * @param {number} percentage The percentage of the game height as a decimal value.
     * @returns {number} The pixel value you should set.
     */
    getTopInGame: function(percentage) {
        return Math.round(this.determineResolution().resolution.height * percentage);
    },

    /**
     * Returns the top value to set if you want your element to be placed the given percentage down within
     * the space below the game (provided that your element resides within the gameWrapper div).
     *
     * @param {number} percentage The percentage of the bottom area height as a decimal value.
     * @returns {number} The pixel value you should set.
     */
    getTopBelowGame: function(percentage) {
        return Math.round(this.determineResolution().resolution.height + percentage * this.getSpaceBelowGame());
    },

    /**
     * Returns the bottom value to set if you want your element to be placed the given percentage up within
     * the space above the game (provided that your element resides within the gameWrapper div).
     *
     * @param {number} percentage The percentage of the top area height as a decimal value.
     * @returns {number} The pixel value you should set.
     */
    getBottomAboveGame: function(percentage) {
        return Math.round(-1 * this.getSpaceAboveGame() * percentage);
    },

    /**
     * Returns the bottom value to set if you want your element to be placed the given percentage up within
     * the game area (provided that your element resides within the gameWrapper div).
     *
     * @param {number} percentage The percentage of the game height as a decimal value.
     * @returns {number} The pixel value you should set.
     */
    getBottomInGame: function(percentage) {
        return Math.round(this.determineResolution().resolution.height * percentage);
    },

    /**
     * Returns the bottom value to set if you want your element to be placed the given percentage up within
     * the space below the game (provided that your element resides within the gameWrapper div).
     *
     * @param {number} percentage The percentage of the bottom area height as a decimal value.
     * @returns {number} The pixel value you should set.
     */
    getBottomBelowGame: function(percentage) {
        var spaceBelowGame = this.getSpaceBelowGame();

        return Math.round(percentage * spaceBelowGame - spaceBelowGame);
    },

    /**
     * Returns the height of the area above the game as seen from within the gameWrapper div.
     *
     * @returns {number} The top area height in pixels.
     */
    getSpaceAboveGame: function() {
        var gameMetrics = Game.stage.getGameContainer().getBoundingClientRect(),
            inverseScale = 1 / Services.scaling.getScale();

        return gameMetrics.top * inverseScale;
    },

    /**
     * Returns the height of the area below the game as seen from within the gameWrapper div.
     *
     * @returns {number} The bottom area height in pixels.
     */
    getSpaceBelowGame: function() {
        var gameMetrics = Game.stage.getGameContainer().getBoundingClientRect(),
            inverseScale = 1 / Services.scaling.getScale(),
            screenSize = Utils.Platform.isStandAlone() ? Utils.Platform.getViewportSize() : Utils.Platform.getViewportInnerSize(),
            bottomPartTop = gameMetrics.top + gameMetrics.height;

        return (screenSize.height - bottomPartTop) * inverseScale;
    },

    /**
     * Returns the height of the game area as seen from within the gameWrapper div.
     *
     * @deprecated 8.0.0. Use Environment.determineResolution().resolution.height instead.
     * @returns {number} The game area height in pixels.
     */
    getGameHeight: function() {
        return this.determineResolution().resolution.height;
    },

    /**
     * Returns the offset from top to the bottom of the screen.
     *
     * @deprecated 8.0.0. Use Environment.determineResolution().resolution.height instead.
     * @returns {number} The bottom area height in pixels.
     */
    getCroppedCanvasTopOffsetToBottom: function() {
        return this.determineResolution().resolution.height;
    },

    /**
     * Returns the height of the area below the game as seen from within the gameWrapper div.
     *
     * @deprecated 8.0.0. Calculate (Environment.determineResolution().resolution - Utils.Platform.getViewportInnerSize().height) instead.
     * @returns {number} The bottom area height in pixels.
     */
    getCroppedViewportBottomOffset: function() {
        var resolution = this.determineResolution().resolution;

        return Math.abs(resolution.height - Utils.Platform.getViewportInnerSize().height);
    },

    /**
     * @deprecated 8.0.0. Not in use.
     * @returns {Array} Supported platforms.
     */
    getSupportedPlatforms: function() {
        return [
            "mobile",
            "mobileLow",
            "tablet",
            "tabletLow",
            "desktop"
        ];
    },

    /**
     * Retrieves a string of the current platform.
     *
     * @returns {string|*} Current platform.
     */
    getCurrentPlatform: function() {
        var platform = "mobile";

        if (Platform.isTabletDevice) {
            platform = "tablet";
        }
        else if (Platform.isDesktopDevice) {
            platform = "desktop";
        }

        if (platform !== "desktop" && Platform.isLowEndDevice) {
            platform += "Low";
        }

        return platform;
    },

    /**
     * Retrieves a string of the current platform without taking adherence to
     * low quality or not.
     *
     * @returns {string|*} Current platform CSS.
     */
    getCurrentPlatformCSS: function() {
        var platform = "mobile";

        if (Platform.isTabletDevice) {
            platform = "tablet";
        }
        else if (Platform.isDesktopDevice) {
            platform = "desktop";
        }

        return platform;
    },

    /**
     * Determines the device orientation.
     *
     * NOTE: Has an iframe override.
     *
     * @deprecated 8.0.0. Use Utils.Platform.getOrientation() instead.
     * @returns {string} The string containing device orientation "landscape" or "portrait".
     */
    orientation: function() {
        return this.getOrientation();
    },

    /**
     * Returns the size of the screen.
     *
     * NOTE: Has an iframe override.
     *
     * @deprecated 8.0.0. Use Utils.Platform.getViewportOuterSize() instead.
     * @returns {Object} The object containing the screen properties (w, h).
     */
    getScreenSize: function() {
        return { width: window.outerWidth, height: window.outerHeight };
    },

    /**
     * Returns the real screen size for the device.
     *
     * @deprecated 8.0.0. Use Utils.Platform methods instead to get the proper viewport size.
     * @returns {Object} The object containing the screen properties (w, h).
     */
    getRealScreenSize: function() {
        var screenWidth,
            screenHeight;

        // Browser doesn't have to be the full screen size on desktop
        if (Platform.isDesktopDevice) {
            return Utils.Platform.getViewportInnerSize();
        }

        if (this.getDeviceOrientation() === "PORTRAIT") {
            screenWidth = Math.min(window.screen.width, window.screen.height);
            screenHeight = Math.max(window.screen.width, window.screen.height);
        }
        else if (this.getDeviceOrientation() === "LANDSCAPE") {
            screenWidth = Math.max(window.screen.width, window.screen.height);
            screenHeight = Math.min(window.screen.width, window.screen.height);
        }

        return { width: screenWidth, height: screenHeight };
    },

    /**
     * Returns the inner size of the screen.
     *
     * NOTE: Has an iframe override.
     *
     * @deprecated 8.0.0. Use Utils.Platform.getViewportInnerSize() instead.
     * @returns {Object} The object containing the inner screen properties (w, h, top).
     */
    getInnerScreenSize: function() {
        return { width: window.innerWidth, height: window.innerHeight };
    },

    /**
     * Get the scale.
     *
     * @deprecated 8.0.0. Moved to Scaling. Use Services.scaling.getScale() instead.
     * @returns {number} The scale as a float.
     */
    getScale: function() {
        return this.scale;
    },

    /**
     * Get the scale.
     *
     * @deprecated 8.0.0. Moved to Scaling. Use Services.scaling.setScale() instead.
     * @param {number} scale The scale as a float.
     * @returns {void}
     */
    setScale: function(scale) {
        this.scale = scale;
    },

    /**
     * Points to browser to a new location through window.location.
     *
     * This is useful because we can override it during testing.
     *
     * @deprecated 8.0.0. Use Sys.utils.setWindowLocation() instead.
     * @param {string} url The URL to point the browser to.
     * @returns {void}
     */
    goTo: function(url) {
        this.fireEvent("request:fullscreen.exit");

        this.setWindowLocation(url);
    },

    /**
     * Changes the window location.
     *
     * @deprecated 8.0.0. Use Sys.utils.setWindowLocation() instead.
     * @param {string} url The Url to the new location.
     * @returns {*} The window location to the specified url.
     */
    setWindowLocation: function(url) {
        // Now we can actually mock redirects in our unit tests
        setTimeout(function() {
            window.location = Sys.utils.sanitizeURL(url);
        }, 300);
    },

    /**
     * Points to browser to a new location through window.location.
     *
     * @deprecated 8.0.0. Use Sys.utils.openNewBrowserTab() instead.
     * @param {string} url The URL to point the browser to.
     * @param {string} name An id for the new tab, will prevent opening of several tabs if one is already open.
     * @returns {void}
     */
    openNewBrowserTab: function(url, name) {
        window.open(url, name).focus();
    },

    /**
     * Calls the goToLobby function with the cashier specific reason (5).
     *
     * @deprecated 8.0.0. Use Sys.utils.goToCashier() instead.
     * @returns {void}
     */
    goToCashier: function() {
        this.goToLobby(5);
    },

    /**
     * Reloads the window.
     *
     * This is useful because we can override it for iFrame specific code.
     *
     * NOTE: Has an iframe override.
     *
     * @deprecated 8.0.0. Use Sys.utils.reload() instead.
     * @returns {void}
     */
    reload: function() {
        window.location.reload();
    },

    /**
     * Returns the event names for a specific type of event or the whole event names objects.
     *
     * @deprecated 8.0.0. Moved to UserInput. Not in use.
     * @param {string} [eventKey] The key for the event name.
     * @returns {string[]|Object} The event names or full event object.
     */
    getInteractionEvents: function(eventKey) {
        if (eventKey) {
            return this.interactionEvents[eventKey] || [];
        }

        return this.interactionEvents;
    },

    /**
     * Returns the event types used for user input.
     *
     * @deprecated 8.0.0. Not in use.
     * @returns {string[]} The event types.
     */
    getEventType: function() {
        return this.eventType;
    },

    /**
     * Checks if event is a start event.
     *
     * @deprecated 8.0.0. Moved to UserInput. Not in use.
     * @param {Object} event The event object.
     * @returns {boolean} Returns true if the condition is met.
     */
    isStartEvent: function(event) {
        var me = this,
            isValidStartEvent = Sys.contains(me.interactionEvents.start, event.type);

        // If we use mouse down, make sure user pressed the left button
        if (event.type === "mousedown" && !me.leftButtonClicked(event)) {
            isValidStartEvent = false;
        }

        return isValidStartEvent;
    },

    /**
     * Checks if event is an end event.
     *
     * @deprecated 8.0.0. Moved to UserInput. Not in use.
     * @param {Object} event The event object.
     * @returns {boolean} Returns true if the condition is met.
     */
    isEndEvent: function(event) {
        var me = this,
            isValidEndEvent = Sys.contains(me.interactionEvents.end, event.type);

        // If we use mouse up, check if user pressed the left button
        if (event.type === "mouseup" && !me.leftButtonClicked(event)) {
            isValidEndEvent = false;
        }

        return isValidEndEvent;
    },

    /**
     * Checks if event is a move event.
     *
     * @deprecated 8.0.0. Moved to UserInput. Not in use.
     * @param {Object} event The event object.
     * @returns {boolean} Returns true if the condition is met.
     */
    isMoveEvent: function(event) {
        return Sys.contains(this.interactionEvents.move, event.type);
    },

    /**
     * Checks if event is a cancel event.
     *
     * @deprecated 8.0.0. Moved to UserInput. Not in use.
     * @param {Object} event The event object.
     * @returns {boolean} Returns true if the condition is met.
     */
    isCancelEvent: function(event) {
        return Sys.contains(this.interactionEvents.cancel, event.type);
    },

    /**
     * Checks if event is a scroll event.
     *
     * @deprecated 8.0.0. Moved to UserInput. Not in use.
     * @param {Object} event The event object.
     * @returns {boolean} Returns true if the condition is met.
     */
    isScrollEvent: function(event) {
        return Sys.contains(this.interactionEvents.scroll, event.type);
    },

    /**
     * Checks if the event is a key up event.
     *
     * @deprecated 8.0.0. Moved to UserInput. Not in use.
     * @param {Object} event The event object.
     * @returns {boolean} Returns true if the condition is met.
     */
    isKeyUpEvent: function(event) {
        return Sys.contains(this.interactionEvents.keyUp, event.type);
    },

    /**
     * Checks if the event is a key down event.
     *
     * @deprecated 8.0.0. Moved to UserInput. Not in use.
     * @param {Object} event The event object.
     * @returns {boolean} Returns true if the condition is met.
     */
    isKeyDownEvent: function(event) {
        return Sys.contains(this.interactionEvents.keyDown, event.type);
    },

    /**
     * Checks if the event is a key press event.
     *
     * @deprecated 8.0.0. Moved to UserInput. Not in use.
     * @param {Object} event The event object.
     * @returns {boolean} Returns true if the condition is met.
     */
    isKeyPressEvent: function(event) {
        return Sys.contains(this.interactionEvents.keyPress, event.type);
    },

    /**
     * Check if browser is touch capable. NOTE: Does not means it has touch.
     *
     * @deprecated 8.0.0. Use Utils.Platform.isTouchSupported() instead.
     * @returns {boolean} True if browser is touch capable.
     */
    isTouchCapable: function() {
        return "ontouchstart" in window ||
                window.DocumentTouch && document instanceof window.DocumentTouch ||
                window.navigator.maxTouchPoints > 0 ||
                window.navigator.msMaxTouchPoints > 0;
    },

    /**
     * Define which type of events to use.
     *
     * @deprecated 8.0.0. Moved to UserInput. Not in use.
     * @returns {void}
     */
    defineInputEvents: function() {
        var me = this,
            start = [],
            move = [],
            end = [],
            cancel = [],
            scroll = [],
            keyUp = [],
            keyDown = [],
            type = [];

        // Check if microsoft prefix pointer events is supported
        // NOTE: prefixed events are now deprecated. Keeping for backwards compatibility
        if (window.navigator.pointerEnabled || window.navigator.msPointerEnabled) {
            start.push(window.navigator.pointerEnabled ? "pointerdown" : "MSPointerDown");
            move.push(window.navigator.pointerEnabled ? "pointermove" : "MSPointerMove");
            end.push(window.navigator.pointerEnabled ? "pointerup" : "MSPointerUp");
            cancel.push(window.navigator.pointerEnabled ? "pointerout" : "MSPointerOut");

            // If it's not a touchscreen we add support for mouse wheel event
            if (!Utils.Platform.isTouchSupported()) {
                scroll.push(me.getMouseWheelEventName());
            }

            type.push("pointerEvent");
        }
        else {
            // check if touch events is supported
            if (Utils.Platform.isTouchSupported()) {
                start.push("touchstart");
                move.push("touchmove");
                end.push("touchend");
                cancel.push("touchcancel");

                type.push("touchEvent");
            }

            // We no longer listen to mouse events for the stock browser since it fires both mouse and touch events on every touch.
            // This resulted in double events being fired, which caused the toggle buttons to break.
            if (!Platform.isAndroidStockBrowser) {
                start.push("mousedown");
                move.push("mousemove");
                end.push("mouseup");
                cancel.push("mouseout");
                scroll.push(me.getMouseWheelEventName());

                type.push("mouseEvent");
            }
        }

        if (me.platform === "desktop") {
            keyUp.push("keyup");
            keyDown.push("keydown");
        }

        me.interactionEvents = {
            start: start,
            move: move,
            end: end,
            cancel: cancel,
            keyUp: keyUp,
            keyDown: keyDown,
            scroll: scroll
        };

        me.eventType = type;
    },

    /**
     * Checks if the left button on the mouse is clicked.
     *
     * @deprecated 8.0.0. Moved to UserInput. Not in use.
     * @param {Event} event The mouse event to test.
     * @returns {boolean} Returns true if left mouse button is clicked.
     */
    leftButtonClicked: function(event) {
        if ("buttons" in event && event.buttons !== 0) {
            return event.buttons === 1;
        }
        else if ("which" in event) {
            return event.which === 1;
        }

        return event.button === 0;
    }
};

Sys.Environment = Sys.extend(Sys.Observable, Sys.Environment, "Sys.Environment");
